import { useCallback, useRef, useState } from 'react'

interface Dimensions {
  width: number
  height: number
}

export function useResizeObserver(threshold = 0) {
  const [dimensions, _setDimensions] = useState<Dimensions>({ width: 0, height: 0 })
  const resizeObserver = useRef<ResizeObserver | null>(null)
  const previousDimensions = useRef<Dimensions>({ width: 0, height: 0 })

  const setDimensions = useCallback(
    ({ width, height }: Dimensions) => {
      const widthDiff = Math.abs(width - previousDimensions.current.width)
      const heightDiff = Math.abs(height - previousDimensions.current.height)

      if (widthDiff >= threshold || heightDiff >= threshold) {
        previousDimensions.current = { width, height }
        _setDimensions({ width, height })
      }
    },
    [threshold]
  )

  const observerRef = useCallback((node: HTMLElement | null) => {
    if (node !== null) {
      // Node is attached
      const { width, height } = node.getBoundingClientRect()
      setDimensions({ width, height })

      resizeObserver.current = new ResizeObserver(entries => {
        if (!entries?.length) return
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      })
      resizeObserver.current.observe(node)
    } else {
      // Node is detached
      if (resizeObserver.current) {
        resizeObserver.current.disconnect()
      }
    }
  }, [])

  return [observerRef, dimensions] as const
}
